import { useEffect, useState, Fragment } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";
import { InviteMembers } from "./InviteMembers";
import { Administrators } from "./Administrators";

export function Members() {
  const [members, setMembers] = useState([]);
  const [activityFor, setActivityFor] = useState(null);
  const [profileFor, setProfileFor] = useState(null);
  const [activity, setActivity] = useState([]);
  useEffect(() => { api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const EVENT_LABELS = {
    registered: "Registered", login: "Login", commitment: "Commitment",
    download: "Document download", listing: "Listing created", interest: "Interest expressed",
    funds_sent: "Funds sent",
  };

  const FLOW_LABELS = { lead: "Institutional lead firms", co: "Institutional co-investors", all: "All available deals" };

  const openActivity = async (m) => {
    if (activityFor === m.user_id) {
      setActivityFor(null);
      return;
    }
    try {
      const { data } = await api.get(`/admin/members/${m.user_id}/activity`);
      setActivity(data);
      setActivityFor(m.user_id);
      setProfileFor(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadStatement = async (m) => {
    try {
      const res = await api.get(`/admin/members/${m.user_id}/statement.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solera-statement-${(m.name || "member").toLowerCase().replace(/[ ,]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const profileRows = (p) => [
    ["Investor type", p.investor_type === "entity" ? `Entity, ${p.entity_name || ""}` : "Individual"],
    ["Accredited", p.accredited ? "Confirmed" : "Not confirmed"],
    ["Regions", (p.regions || []).join(", ")],
    ["Stages", (p.stages || []).join("; ")],
    ["Deal flow", FLOW_LABELS[p.deal_flow] || p.deal_flow],
    ["Sectors", (p.sectors || []).length >= 38 ? "All sectors" : (p.sectors || []).join(", ") || "-"],
    ["Secondaries", p.secondary_interest ? "Interested" : "Not interested"],
    ["Venture funds", ({ true: "Interested", false: "Not interested" })[p.venture_funds] ?? "-"],
    ["Fund sizes", (p.fund_sizes || []).join("; ") || "-"],
    ["Manager experience", p.manager_experience || "-"],
    ["Other asset classes", (p.other_asset_classes || []).join(", ") || "-"],
  ];

  return (
    <div className="space-y-12">
      <Administrators />
      <InviteMembers />
      <table className="table-solera" data-testid="admin-members-table">
      <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Onboarded</th><th>Referrer</th><th>Joined</th><th></th></tr></thead>
      <tbody>
        {members.map((m) => (
          <Fragment key={m.user_id}>
            <tr>
              <td className="text-bone">{m.name}</td>
              <td className="text-muted-s">{m.email}</td>
              <td className="text-muted-s">{m.role}</td>
              <td className="text-muted-s">{m.onboarded ? "Yes" : "No"}</td>
              <td className="text-muted-s">{m.referrer || ""}</td>
              <td className="text-muted-s t-small fig">{m.created_at?.slice(0, 10)}</td>
              <td className="text-right whitespace-nowrap space-x-4">
                <button onClick={() => { setProfileFor(profileFor === m.user_id ? null : m.user_id); setActivityFor(null); }}
                  className="t-label hover:!text-[#E8E6E1]" data-testid={`profile-member-${m.user_id}`}>
                  {profileFor === m.user_id ? "Hide" : "Profile"}
                </button>
                <button onClick={() => openActivity(m)} className="t-label hover:!text-[#E8E6E1]" data-testid={`activity-member-${m.user_id}`}>
                  {activityFor === m.user_id ? "Hide" : "Activity"}
                </button>
                {m.role === "member" && (
                  <button onClick={() => downloadStatement(m)} className="t-label hover:!text-[#E8E6E1]" data-testid={`statement-member-${m.user_id}`}>Statement</button>
                )}
              </td>
            </tr>
            {profileFor === m.user_id && (
              <tr>
                <td colSpan={7}>
                  <div className="py-4 max-w-2xl" data-testid={`member-profile-${m.user_id}`}>
                    <span className="t-label">Onboarding profile, {m.name}</span>
                    {m.preferences ? (
                      <div className="mt-3 space-y-0">
                        {profileRows(m.preferences).map(([k, v]) => (
                          <div key={k} className="hairline py-3 flex gap-8">
                            <span className="t-label w-44 whitespace-nowrap">{k}</span>
                            <span className="t-small text-bone">{v}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="t-small text-muted-s hairline mt-3 py-3">Onboarding not completed yet.</p>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {activityFor === m.user_id && (
              <tr>
                <td colSpan={7}>
                  <div className="py-4" data-testid={`member-activity-${m.user_id}`}>
                    <span className="t-label">Activity, {m.name}</span>
                    <div className="mt-3 space-y-0">
                      {activity.map((a) => (
                        <div key={a.activity_id} className="hairline py-3 flex gap-8 items-baseline">
                          <span className="t-cap text-muted-s fig whitespace-nowrap w-36">{a.created_at?.slice(0, 16).replace("T", " ")} UTC</span>
                          <span className="t-label w-44 whitespace-nowrap">{EVENT_LABELS[a.event] || a.event}</span>
                          <span className="t-small text-bone">{a.detail}</span>
                        </div>
                      ))}
                      {activity.length === 0 && <p className="t-small text-muted-s hairline py-3">No recorded activity yet.</p>}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
    </div>
  );
}
